// Page-level display pieces: EmptyState (Cards.swift EmptyState on the soft hero mesh), PageHeader (title, subtitle,
// actions), KpiTile (Cards.swift StatTile: icon badge, big rounded numeral, caps title, subtitle or delta) and Stat
// (an inline label/value pair for headers such as "Open pipeline $12,300").

import { ArrowDownRight, ArrowRight, ArrowUpRight, ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { IconBadge } from './Card';
import { cn } from './cn';
import { Skeleton } from './Feedback';

export interface EmptyStateProps {
  icon?: ReactNode;
  /** Hex for the icon badge (navy by default). */
  color?: string;
  title: ReactNode;
  message?: ReactNode;
  /** Buttons or links under the message. */
  action?: ReactNode;
  /** Drops the mesh backdrop and padding for use inside a card or table. */
  compact?: boolean;
  className?: string;
}

export function EmptyState({ icon, color, title, message, action, compact, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center text-center animate-fade-in',
        compact ? 'gap-2 px-4 py-8' : 'gt-hero-soft gap-3 rounded-sheet px-6 py-12',
        className,
      )}
    >
      {icon != null && (
        <IconBadge
          icon={icon}
          color={color}
          size={compact ? 44 : 64}
          className="shadow-[0_6px_12px_rgb(16_42_94/0.12)]"
        />
      )}
      <h3 className={cn('font-semibold text-fg', compact ? 'text-base' : 'text-lg')}>{title}</h3>
      {message != null && <p className="max-w-md text-sm text-fg-muted">{message}</p>}
      {action != null && <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side: primary actions for the page. */
  actions?: ReactNode;
  /** A "back" link above the title (detail pages). */
  back?: { to: string; label: string };
  /** Something between the title and the actions (a status chip). */
  badge?: ReactNode;
  /** Content under the title row (tabs, filters). */
  children?: ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, actions, back, badge, children, className }: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-3 pb-4', className)}>
      {back && (
        <Link
          to={back.to}
          className="-ml-1 inline-flex w-fit items-center gap-0.5 rounded-md px-1 text-sm font-medium text-link hover:underline"
        >
          <ChevronLeft aria-hidden className="size-4" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl leading-tight font-bold tracking-tight text-fg">{title}</h1>
            {badge}
          </div>
          {subtitle != null && <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>}
        </div>
        {actions != null && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export interface KpiDelta {
  /** Display text ("+12%", "3 more"). */
  label: string;
  direction: 'up' | 'down' | 'flat';
  /** Whether this direction is good news (green) or bad (red); flat is neutral. Default: up is good. */
  positive?: boolean;
}

export interface KpiTileProps {
  label: string;
  value: ReactNode;
  /** Secondary line ("38% contact rate", "$12,400 weighted"). */
  sub?: ReactNode;
  delta?: KpiDelta;
  icon?: ReactNode;
  /** Hex for the icon badge. */
  color?: string;
  /** Click-through (Dashboard tiles open the filtered Stores list). */
  to?: string;
  onClick?: () => void;
  loading?: boolean;
  className?: string;
}

export function KpiTile({ label, value, sub, delta, icon, color, to, onClick, loading, className }: KpiTileProps) {
  const good = delta ? (delta.direction === 'flat' ? null : (delta.positive ?? delta.direction === 'up')) : null;
  const DeltaIcon = delta?.direction === 'up' ? ArrowUpRight : delta?.direction === 'down' ? ArrowDownRight : ArrowRight;
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        {icon != null ? <IconBadge icon={icon} color={color} size={30} /> : <span />}
        {delta && !loading && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
              good === null ? 'bg-surface-3 text-fg-muted' : good ? 'bg-success-soft text-success-text' : 'bg-danger-soft text-danger-text',
            )}
          >
            <DeltaIcon aria-hidden className="size-3" />
            {delta.label}
          </span>
        )}
      </div>
      {loading ? (
        <Skeleton className="mt-1 h-7" width="55%" />
      ) : (
        <div className="truncate font-rounded text-[26px] leading-tight font-bold text-fg tabular-nums">{value}</div>
      )}
      <div className="truncate text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">{label}</div>
      {sub != null && !loading && <div className="line-clamp-2 text-xs text-fg-muted">{sub}</div>}
    </>
  );
  const classes = cn(
    'flex min-w-0 flex-col gap-1.5 rounded-card border border-line bg-surface p-3 text-left shadow-card',
    (to || onClick) && 'transition-[box-shadow,transform] duration-150 hover:shadow-raised active:scale-[0.99]',
    className,
  );
  if (to) {
    return (
      <Link to={to} className={classes} aria-busy={loading || undefined}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes} aria-busy={loading || undefined}>
        {body}
      </button>
    );
  }
  return (
    <div className={classes} aria-busy={loading || undefined}>
      {body}
    </div>
  );
}

export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  align?: 'start' | 'end';
  size?: 'sm' | 'md';
  className?: string;
}

/** Inline label over value (page headers, column headers, summary rows). */
export function Stat({ label, value, sub, align = 'start', size = 'md', className }: StatProps) {
  return (
    <div className={cn('flex min-w-0 flex-col', align === 'end' ? 'items-end text-right' : 'items-start', className)}>
      <span className="text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">{label}</span>
      <span
        className={cn(
          'font-rounded font-bold text-fg tabular-nums',
          size === 'sm' ? 'text-base leading-6' : 'text-xl leading-7',
        )}
      >
        {value}
      </span>
      {sub != null && <span className="text-xs text-fg-muted">{sub}</span>}
    </div>
  );
}

export interface PageContainerProps {
  /** `default` for forms and detail pages, `wide` for tables and dashboards, `full` for edge-to-edge (map). */
  width?: 'narrow' | 'default' | 'wide' | 'full';
  className?: string;
  children: ReactNode;
}

const PAGE_WIDTH = {
  narrow: 'max-w-3xl',
  default: 'max-w-6xl',
  wide: 'max-w-[1600px]',
  full: 'max-w-none',
} as const;

/** Standard page gutter inside the shell's scroll area, so every screen lines up with the rail. */
export function PageContainer({ width = 'wide', className, children }: PageContainerProps) {
  return (
    <div className={cn('mx-auto w-full px-4 py-5 sm:px-6 lg:px-8 lg:py-6', PAGE_WIDTH[width], className)}>
      {children}
    </div>
  );
}
